import { useParams } from "react-router-dom";

import Box from "@/components/Box";
import {
  AdditionalInfo,
  QUESTION_SORT_OPTIONS,
  QuestionList,
  useSortedQuestions
} from "@/components/QuestionnaireReport";
import Text from "@/components/Text";
import PartnerDashboardBox from "@/components/boxes/PartnerDashboardBox";
import Template from "@/components/patientView/shared/Template";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import useDisclosure from "@/hooks/useDisclosure";
import useListControls from "@/hooks/useListControls";
import SortMenu from "@/tpo/SortMenu";
import Stack from "@/tpo/Stack";
import ButtonV2 from "@/v2/Buttons";
import { useQuery } from "@apollo/client";
import { useTheme } from "styled-components";

import { USER_QUESTIONNAIRE_REPORT_QUERY } from "./graphql/queries";
import RequestQuestionnaireModal from "./RequestQuestionnaireModal";

export default function PatientQuestionnaire({ templateProps }) {
  const { patientId } = useParams();
  const { organisationId } = useOrganisationContext();

  const { data, loading } = useQuery(USER_QUESTIONNAIRE_REPORT_QUERY, {
    variables: {
      userId: patientId,
      organisationId
    },
    skip: !patientId || !organisationId
  });

  const report = data?.userQuestionnaireReport;
  const hasGuardian =
    data?.user?.patientProfile?.isWard && !!data?.user?.patientProfile?.guardian;
  const isActive = hasGuardian
    ? data?.user?.patientProfile?.guardian?.user?.isActive
    : data?.user?.isActive;
  const hasAnswers = report?.hasAnswers;
  const questions = report?.userQuestions || [];
  const theme = useTheme();
  const controls = useListControls({ sortOptions: QUESTION_SORT_OPTIONS });
  const sortedQuestions = useSortedQuestions(questions, controls.sort);
  const [showModal, { open: openModal, close: closeModal }] = useDisclosure();

  return (
    <Template selectedTab="questionnaire" docTitle="Questionnaire" {...templateProps}>
      <Box bg="haze" px={20} pt={theme.spacing.section.pt} pb={theme.spacing.section.pb}>
        <Stack maxWidth={1532} mx="auto" gap={40}>
          <Box stacked gap={[20]} maxWidth={720} mx="auto">
            <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]}>
              Self assessment
            </Box>
            <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} pt={16}>
              Find below your patients responses to the symptoms questionnaire Please encourage your
              patient for a regular update every 3-6 months to keep it up to date.
            </Box>
          </Box>
          {!loading && (
            <Box display="flex" justifyContent="flex-end" maxWidth={1024} mx="auto" width="100%">
              <SortMenu
                value={controls.sort}
                setValue={controls.setSort}
                open={controls.sortMenuOpen}
                setOpen={controls.setSortMenuOpen}
                options={controls.sortOptions}
                placeholder="Sort By"
                styles={{ button: { color: "white", sx: { color: "dark" } } }}
              />
            </Box>
          )}
          <PartnerDashboardBox
            pt={[60]}
            pb={[80]}
            borderRadius={20}
            maxWidth={1024}
            innerMaxWidth={944}
          >
            {loading ? (
              <Text>Loading...</Text>
            ) : (
              <Box display="flex" flexDirection="column">
                <Box fontFamily="gilroyBold" fontSize={[18]}>
                  Latest submission:{" "}
                  {report.submittedAt
                    ? new Date(report.submittedAt).toLocaleDateString()
                    : "No submission"}
                </Box>
                <Box pt={24}>
                  <ButtonV2 color="dark" onClick={openModal} size={["sm", "sm", "md"]}>
                    {hasAnswers ? "Request retake" : "Request questionnaire"}
                  </ButtonV2>
                </Box>
                <AdditionalInfo
                  title="Additional patient information"
                  titleProps={{ fontSize: [24] }}
                  mt={28}
                  mb={2}
                >
                  {report.additionalInformation}
                </AdditionalInfo>
                <QuestionList questions={sortedQuestions} />
              </Box>
            )}
          </PartnerDashboardBox>
        </Stack>
      </Box>
      <RequestQuestionnaireModal
        show={showModal}
        onClose={closeModal}
        patientId={patientId}
        organisationId={organisationId}
        isActive={isActive}
        hasGuardian={hasGuardian}
      />
    </Template>
  );
}
